// 直接Cognito认证工具（绕过Amplify）
import { CLIENT_ID, CLIENT_SECRET } from '../config/amplify';

// 计算SECRET_HASH（使用Web Crypto API）
export async function calculateSecretHash(username) {
  const message = username + CLIENT_ID;
  const encoder = new TextEncoder();
  const keyData = encoder.encode(CLIENT_SECRET);
  const messageData = encoder.encode(message);
  
  // 导入密钥
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  // 计算HMAC
  const signature = await crypto.subtle.sign('HMAC', key, messageData);
  
  // 转换为Base64
  return btoa(String.fromCharCode(...new Uint8Array(signature)));
}

// 直接调用Cognito API
export async function cognitoRequest(action, params) {
  const response = await fetch(`https://cognito-idp.us-east-1.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
      'Content-Type': 'application/x-amz-json-1.1',
    },
    body: JSON.stringify(params)
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Cognito API Error');
  }
  
  return data;
}

// 登录函数
export async function signIn(username, password) {
  try {
    console.log('🔐 直接Cognito登录...');
    
    // 计算SECRET_HASH
    const secretHash = await calculateSecretHash(username);
    console.log('🔑 SECRET_HASH计算成功');
    
    // 调用InitiateAuth
    const result = await cognitoRequest('InitiateAuth', {
      ClientId: CLIENT_ID,
      AuthFlow: 'USER_PASSWORD_AUTH',
      AuthParameters: {
        USERNAME: username,
        PASSWORD: password,
        SECRET_HASH: secretHash
      }
    });
    
    console.log('✅ 登录成功!');
    
    // 保存tokens
    if (result.AuthenticationResult) {
      localStorage.setItem('accessToken', result.AuthenticationResult.AccessToken);
      localStorage.setItem('idToken', result.AuthenticationResult.IdToken);
      localStorage.setItem('refreshToken', result.AuthenticationResult.RefreshToken);
      
      // 解析用户信息
      const idToken = result.AuthenticationResult.IdToken;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      
      return {
        success: true,
        user: {
          username: payload.email,
          attributes: {
            email: payload.email,
            given_name: payload.given_name,
            family_name: payload.family_name,
            phone_number: payload.phone_number,
            sub: payload.sub
          }
        },
        tokens: result.AuthenticationResult
      };
    }
    
    throw new Error('No authentication result');
    
  } catch (error) {
    console.error('❌ 登录失败:', error);
    throw error;
  }
}

// 注册函数
export async function signUp(username, password, attributes) {
  try {
    console.log('🔐 直接Cognito注册...');
    
    // 计算SECRET_HASH
    const secretHash = await calculateSecretHash(username);
    
    // 准备用户属性
    const userAttributes = [];
    Object.entries(attributes).forEach(([key, value]) => {
      if (value) {
        userAttributes.push({
          Name: key,
          Value: value
        });
      }
    });
    
    // 调用SignUp
    const result = await cognitoRequest('SignUp', {
      ClientId: CLIENT_ID,
      Username: username,
      Password: password,
      SecretHash: secretHash,
      UserAttributes: userAttributes
    });
    
    console.log('✅ 注册成功!');
    return {
      success: true,
      userSub: result.UserSub,
      codeDeliveryDetails: result.CodeDeliveryDetails
    };
    
  } catch (error) {
    console.error('❌ 注册失败:', error);
    throw error;
  }
}

// 确认注册
export async function confirmSignUp(username, code) {
  try {
    console.log('🔐 确认注册...');
    
    // 计算SECRET_HASH
    const secretHash = await calculateSecretHash(username);
    
    // 调用ConfirmSignUp
    await cognitoRequest('ConfirmSignUp', {
      ClientId: CLIENT_ID,
      Username: username,
      ConfirmationCode: code,
      SecretHash: secretHash
    });
    
    console.log('✅ 确认成功!');
    return {
      success: true
    };
    
  } catch (error) {
    console.error('❌ 确认失败:', error);
    throw error;
  }
}

// 登出函数（本地清理）
export async function signOut() {
  console.log('🚪 退出登录...');
  
  // 清除所有tokens
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('authToken');
  localStorage.removeItem('token');
  
  console.log('✅ 已清除所有认证信息');
  
  return { success: true };
}

// 忘记密码
export async function forgotPassword(username) {
  try {
    console.log('🔐 发送密码重置验证码...');
    
    // 计算SECRET_HASH
    const secretHash = await calculateSecretHash(username);
    
    // 调用ForgotPassword
    const result = await cognitoRequest('ForgotPassword', {
      ClientId: CLIENT_ID,
      Username: username,
      SecretHash: secretHash
    });
    
    console.log('✅ 验证码已发送!');
    return {
      success: true,
      codeDeliveryDetails: result.CodeDeliveryDetails
    };
    
  } catch (error) {
    console.error('❌ 发送验证码失败:', error);
    throw error;
  }
}

// 确认忘记密码（重置密码）
export async function confirmForgotPassword(username, code, newPassword) {
  try {
    console.log('🔐 重置密码...');
    
    // 计算SECRET_HASH
    const secretHash = await calculateSecretHash(username);
    
    // 调用ConfirmForgotPassword
    await cognitoRequest('ConfirmForgotPassword', {
      ClientId: CLIENT_ID,
      Username: username,
      ConfirmationCode: code,
      Password: newPassword,
      SecretHash: secretHash
    });
    
    console.log('✅ 密码重置成功!');
    return {
      success: true
    };
    
  } catch (error) {
    console.error('❌ 密码重置失败:', error);
    throw error;
  }
}

// 从后端获取完整的用户信息（包括员工信息）
export async function fetchUserProfile() {
  try {
    const idToken = localStorage.getItem('idToken');
    
    if (!idToken) {
      return null;
    }
    
    // 调用后端API获取完整用户信息
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const response = await fetch(`${apiUrl}/user-management/profile`, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.error('获取用户信息失败:', response.status);
      return null;
    }
    
    const result = await response.json();
    
    if (result.success && result.data) {
      // 将后端的数据格式转换为前端期望的格式
      return {
        username: result.data.email,
        attributes: {
          email: result.data.email,
          given_name: result.data.first_name,
          family_name: result.data.last_name,
          phone_number: result.data.phone_number,
          sub: result.data.userId
        },
        // 额外的数据库字段
        isEmployee: result.data.isEmployee,
        employeeRole: result.data.employeeRole,
        employeeId: result.data.employeeId,
        credits: result.data.credits
      };
    }
    
    return null;
  } catch (error) {
    console.error('获取用户信息失败:', error);
    return null;
  }
}

// 用 refreshToken 刷新 accessToken 和 idToken
export async function refreshTokens() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    console.log('⚠️ 无 refreshToken，无法刷新');
    return false;
  }

  try {
    console.log('🔄 尝试用 refreshToken 刷新...');

    // Cognito REFRESH_TOKEN_AUTH 不需要 SECRET_HASH（看 pool 配置）
    // 但有些 pool 需要，我们都试试
    const params = {
      ClientId: CLIENT_ID,
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      AuthParameters: {
        REFRESH_TOKEN: refreshToken
      }
    };

    // 如果有 CLIENT_SECRET，需要用 refreshToken 对应的 username 算 hash
    // 从现有 idToken 里取 username
    const oldIdToken = localStorage.getItem('idToken');
    if (oldIdToken && CLIENT_SECRET) {
      try {
        const oldPayload = JSON.parse(atob(oldIdToken.split('.')[1]));
        const username = oldPayload.email || oldPayload.sub || oldPayload['cognito:username'];
        if (username) {
          const secretHash = await calculateSecretHash(username);
          params.AuthParameters.SECRET_HASH = secretHash;
        }
      } catch (e) {
        // 忽略，试试不带 hash
      }
    }

    const result = await cognitoRequest('InitiateAuth', params);

    if (result.AuthenticationResult) {
      localStorage.setItem('accessToken', result.AuthenticationResult.AccessToken);
      localStorage.setItem('idToken', result.AuthenticationResult.IdToken);
      // refreshToken 不一定会返回新的，保留旧的
      if (result.AuthenticationResult.RefreshToken) {
        localStorage.setItem('refreshToken', result.AuthenticationResult.RefreshToken);
      }
      console.log('✅ Token 刷新成功!');
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Token 刷新失败:', error);
    return false;
  }
}

// 获取当前用户（从token）- 如果过期自动尝试刷新
export async function getCurrentUser() {
  const idToken = localStorage.getItem('idToken');
  
  if (!idToken) {
    return null;
  }
  
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    
    // 检查token是否过期
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      console.log('⚠️ Token 已过期，尝试刷新...');
      
      // 尝试刷新 token
      const refreshed = await refreshTokens();
      if (refreshed) {
        // 刷新成功，用新 token 重新解析
        const newIdToken = localStorage.getItem('idToken');
        const newPayload = JSON.parse(atob(newIdToken.split('.')[1]));
        return {
          username: newPayload.email,
          attributes: {
            email: newPayload.email,
            given_name: newPayload.given_name,
            family_name: newPayload.family_name,
            phone_number: newPayload.phone_number,
            sub: newPayload.sub
          }
        };
      } else {
        // 刷新失败，才登出
        console.log('❌ 刷新失败，需重新登录');
        await signOut();
        return null;
      }
    }
    
    // Token 未过期，但快过期了（5分钟内），后台静默刷新
    if (payload.exp && payload.exp - now < 300) {
      console.log('⏰ Token 即将过期，后台刷新...');
      refreshTokens().catch(() => {}); // 静默刷新，不阻塞
    }
    
    return {
      username: payload.email,
      attributes: {
        email: payload.email,
        given_name: payload.given_name,
        family_name: payload.family_name,
        phone_number: payload.phone_number,
        sub: payload.sub
      }
    };
  } catch (error) {
    console.error('解析token失败:', error);
    return null;
  }
}
